/**
 * QCollab Chatter integration wrapper for QOps.
 *
 * Handles token exchange, user mapping, and renders the @qcollab/chatter widget.
 * The QCollab API URL comes from VITE_QCOLLAB_API_URL env var.
 */
import { QCollabChatter } from '@qcollab/chatter';
import type { ChatterUser } from '@qcollab/chatter';
import { useCollabToken } from '@/hooks/useCollabToken';
import { useAuthStore } from '@/stores/authStore';
import { Skeleton } from '@/components/ui';
import { ApiError } from '@/services/api';

const QCOLLAB_API_URL =
  import.meta.env.VITE_QCOLLAB_API_URL || 'http://localhost:8002/api/v1';

interface CollabChatterProps {
  entityType: string;
  entityId: string;
  height?: string | number;
  className?: string;
}

export function CollabChatter({ entityType, entityId, height = '500px', className }: CollabChatterProps) {
  const qopsUser = useAuthStore((s) => s.user);
  const { data: tokenData, isLoading, isError, error } = useCollabToken(!!qopsUser);

  if (!qopsUser) {
    return (
      <div className="space-y-3 p-4">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-4 w-32" />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-3 p-4">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-4 w-32" />
      </div>
    );
  }

  if (isError || !tokenData) {
    return (
      <div className="flex flex-col items-center justify-center p-6 text-sm text-gray-500 border rounded-lg border-dashed">
        <p className="font-medium">QCollab no disponible</p>
        <p className="mt-1 text-xs">
          {isError
            ? error instanceof ApiError && error.status === 503
              ? 'Integracion QCollab no configurada en este servidor'
              : error instanceof ApiError && error.status === 502
                ? 'QCollab no disponible temporalmente — intente de nuevo'
                : (error as Error)?.message || 'Error al conectar con QCollab'
            : 'Integracion QCollab no configurada'}
        </p>
      </div>
    );
  }

  const currentUser: ChatterUser = {
    id: String(qopsUser.id),
    displayName: qopsUser.apellido
      ? `${qopsUser.nombre} ${qopsUser.apellido}`
      : qopsUser.nombre,
    avatarUrl: qopsUser.avatar_url ?? null,
  };

  return (
    <QCollabChatter
      entityType={entityType}
      entityId={entityId}
      currentUser={currentUser}
      apiUrl={QCOLLAB_API_URL}
      token={tokenData.token}
      showFollowers
      allowInternalNotes={qopsUser.tipo === 'INTERNO'}
      height={height}
      className={className}
    />
  );
}
